import { refreshTokenApi } from "@/api/services/main/auth";
import { logout } from "@/utils/logout";
import Cookies from "js-cookie";

type TokenChangeCallback = (token: string | null) => void;

class AuthTokenManager {
  private static instance: AuthTokenManager;
  private listeners: Set<TokenChangeCallback> = new Set();
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  private constructor() {}

  static getInstance(): AuthTokenManager {
    if (!AuthTokenManager.instance) {
      AuthTokenManager.instance = new AuthTokenManager();
    }
    return AuthTokenManager.instance;
  }

  getToken(): string | null {
    return Cookies.get("match_token") || null;
  }

  onTokenChange(callback: TokenChangeCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyTokenChange(token: string | null): void {
    this.listeners.forEach((cb) => cb(token));
  }

  async refreshToken(): Promise<string | null> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<string | null> {
    const accessToken = this.getToken();
    const refreshToken = Cookies.get("refresh_token");
    const matchLicenseNumber = Cookies.get("matchLicenseNumber");

    if (!accessToken || !refreshToken || !matchLicenseNumber) {
      this.clearTokens();
      return null;
    }

    try {
      await refreshTokenApi({ accessToken, refreshToken, matchLicenseNumber });
      const newToken = this.getToken();
      this.notifyTokenChange(newToken);
      return newToken;
    } catch {
      // Refresh failed (incl. admin password reset blocking the refresh): log out silently
      this.clearTokens();
      return null;
    }
  }

  clearTokens(): void {
    logout();
    this.notifyTokenChange(null);
  }

  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }
}

export const authTokenManager = AuthTokenManager.getInstance();
